import DashboardPage from "./dashboard/DashboardPage";
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";


// --- 1. IMPORT THE USER CONTEXT HOOK ---
import { useUserContext } from "./components/context/UserContext";
import LandingPage from "./components/LandingPage";
import HomePage from "./components/HomePage";
import InvoiceTemplates from "./components/InvoiceTemplates";
import QuotationTemplates from "./components/QuotationTemplates";
import ContactAdminTemplate from "./components/ContactAdminTemplate";
import { ContactAdminRedirect, InvoiceAdminRedirect } from "./components/context/UserContext";
import GeneratedDocuments from "./components/GeneratedDocuments";
import InTemplate from "./components/InTemplate";
import QTemplate from "./components/QTemplate";
import CreditNote from "./components/CreditNote";
import SignIn from "./components/SignIn";
import SignUp from "./components/SignUp";
import { UserProvider } from "./components/context/UserContext";

// Redirect admin users to QTemplate or InTemplate, show ContactAdminTemplate for non-admins
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingChatbot from "./components/FloatingChatbot";
import ProductHierarchyManager from "./components/AddProduct.js";

import SelectInvoiceLevelProducts from "./components/SelectInvoiceLevelProduct";
import SelectQuotationLevelProducts from "./components/SelectQuotationLevelProducts";
import SelectLevelProducts from "./components/SelectLevelProducts";
import SelectCreditNotes from "./components/SelectCreditNotes";
import { QuotationProvider } from "./components/context/QuotationContext.js";
import { InvoiceProvider } from "./components/context/InvoiceContext.js";

import QuotationList from "./components/QuotationList.js";
import InvoiceList from "./components/InvoiceList.js";
import TenantManagement from "./components/TenantManagement.js";
import TncReview from "./pages/TncReview.jsx";
import ProfileLegal from "./pages/ProfileLegal.jsx";
import LegalSettingsAdmin from "./pages/LegalSettingsAdmin.jsx";
import TenantPurgeAdmin from "./pages/TenantPurgeAdmin.jsx";
import SuperAdminRoute from "./components/SuperAdminRoute.jsx";
import TncGate from "./components/TncGate.jsx";
import UserManagement from "./components/UserManagement.js";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import ThirdPartyLibraries from "./components/ThirdPartyLibraries";
import { AuthProvider } from "./AuthContext.js";
import AdminTest from "./components/AdminTest.js";
import ViewProducts from "./components/ViewProducts.js";
import TaxReport from "./components/TaxReport";
import ListPage from "./web/accounts/expenses/ListPage";
import CardForm from "./web/accounts/expenses/CardForm";
import TransactionManager from "./web/accounts/expenses/TransactionManager.js";
import TransactionForm from "./web/accounts/expenses/TransactionForm";
import AccountingConfig from "./web/accounts/config/AccountingConfig";

// --- BLOCKED USER ROUTE ---
// This route blocks users whose email is in the DB but who are not signed up
const BlockedUserRoute = ({ children }) => {
  const { userId } = useUserContext();
  const [blocked, setBlocked] = useState(false);
  
  useEffect(() => {
    // Simulate check: if userId exists but not signed up, block
    // Replace this with your real check (e.g. check a flag in user profile)
    if (userId && userId.startsWith("blocked:")) {
      setBlocked(true);
    } else {
      setBlocked(false);
    }
  }, [userId]);
  
  if (blocked) {
    alert("You need to sign up to access this application.");
    return <Navigate to="/signup" replace />;
  }
  
  return children;
};

// --- 2. DEFINE THE ADMIN ROUTE PROTECTION COMPONENT ---
// This component checks for admin status and redirects if the user is not an admin.
const AdminRoute = ({ children }) => {
  const { isAdmin } = useUserContext();

  // if (!isAdmin) {
  //   // If the user is not an admin, redirect them to the homepage.
  //   return <Navigate to="/homepage" replace />;
  // }

  // If the user is an admin, render the component they are trying to access.
  return children;
};

function App() {
  return (
    <AuthProvider>
      <QuotationProvider>
        <InvoiceProvider>
          <UserProvider>
            <Router>
              <Routes>
                 <Route path="/accounting/config" element={<><Navbar /><AccountingConfig /><FloatingChatbot /><Footer /></>} />
                <Route path="/" element={<LandingPage />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/" element={<Navigate to="/signin" replace />} />
                <Route path="/signup" element={<SignUp />} />
                <Route
                  path="/*"
                  element={
                    <>
                      <Navbar />
                      <TncGate>
                      <Routes>
                        {/* --- PUBLIC ROUTES --- */}
                        <Route path="/homepage" element={<HomePage />} />
                        <Route path="/documents" element={<GeneratedDocuments />} />
                        <Route path="/GeneratedDocuments" element={<GeneratedDocuments />} />
                        <Route path="/ViewProducts" element={<ViewProducts />} />
                        <Route path="/invoice-templates/InTemplate" element={<InTemplate />} />
                        <Route path="/creditNote" element={<CreditNote />} />
                        <Route path="/select-credit-notes" element={<SelectCreditNotes />} />
                        <Route path="/contact-admin" element={<ContactAdminTemplate />} />
                        <Route path="/select-quotation-products" element={<SelectQuotationLevelProducts />} />
                        <Route path="/SelectProduct" element={<SelectLevelProducts />} />
                        <Route path="/QuotationList" element={<QuotationList />} />
                        <Route path="/InvoiceList" element={<InvoiceList />} />
                        <Route path="/select-invoice-products" element={<SelectInvoiceLevelProducts />} />

                        {/* --- 3. ACCOUNTING ROUTES (AVAILABLE TO ALL AUTHENTICATED USERS) --- */}
                        <Route path="/accounting/expenses" element={<ListPage />} />
                        <Route path="/accounting/expenses/new" element={<CardForm />} />
                        <Route path="/accounting/expenses/edit/:id" element={<CardForm />} />
                        <Route path="/accounting/transactions" element={<TransactionManager />} />
                        <Route path="/accounting/transactions/new" element={<TransactionForm />} />
                        <Route path="/accounting/config" element={<><Navbar /><AccountingConfig /><FloatingChatbot /><Footer /></>} />
                        <Route path="/tax-report" element={<><TaxReport /><FloatingChatbot /></>} />
                        <Route path="/accounting/gst" element={<><TaxReport /><FloatingChatbot /></>} />
                        <Route path="/tnc" element={<TermsAndConditions />} />
                        <Route path="/TermsAndConditions" element={<TermsAndConditions />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/third-party-libraries" element={<ThirdPartyLibraries />} />
                         <Route path="/dashboard" element={<DashboardPage />} />
                        
                        {/* --- 4. PROTECTED ADMIN ROUTES --- */}
                        <Route path="/admin/expenses/new" element={<AdminRoute><CardForm /></AdminRoute>} />
                        <Route path="/admin/expenses/edit/:id" element={<AdminRoute><CardForm /></AdminRoute>} />
                        <Route path="/invoice-templates" element={<AdminRoute><InvoiceTemplates /></AdminRoute>} />
                        <Route path="/quotation-templates" element={<AdminRoute><QuotationTemplates /></AdminRoute>} />
                        <Route path="/quotation-templates/QTemplate" element={<QTemplate />} />
                        <Route path="/quotation-templates/InTemplate" element={<InTemplate />} />
                        <Route path="/quotation-templates/contact-admin" element={<ContactAdminRedirect />} />
                        <Route path="/invoice-templates/contact-admin" element={<InvoiceAdminRedirect />} />
                        <Route path="/AddProduct" element={<AdminRoute><ProductHierarchyManager /></AdminRoute>} />
                        <Route path="/admin-test" element={<AdminRoute><AdminTest /></AdminRoute>} />
                        <Route path="/UserManagement" element={<AdminRoute><UserManagement /></AdminRoute>} />
                        <Route path="/admin/user-management" element={<AdminRoute><UserManagement /></AdminRoute>} />
                        <Route path="/admin/items" element={<AdminRoute><ListPage /></AdminRoute>} />
                        
                        {/* --- 4. REMOVED TENANT MANAGEMENT ROUTES --- */}
                        <Route path="/TenantManagement" element={<TenantManagement />} />
                        <Route path="/tnc-review" element={<TncReview />} />
                        <Route path="/profile-legal" element={<ProfileLegal />} />
                        <Route path="/legal-settings" element={<SuperAdminRoute><LegalSettingsAdmin /></SuperAdminRoute>} />
                        <Route path="/tenant-purge" element={<SuperAdminRoute><TenantPurgeAdmin /></SuperAdminRoute>} />
                       

                      </Routes>
                      </TncGate>
                      <FloatingChatbot />
                      <Footer />
                    </>
                  }
                />
              </Routes>
            </Router>
          </UserProvider>
        </InvoiceProvider>
      </QuotationProvider>
    </AuthProvider>
  );
}

export default App;